import {type AxiosInstance, type AxiosRequestConfig} from "axios";
import {createApi} from "../api/createApi.ts";
import {type EditableGarageModel} from "../model/garage/GarageDetailModel.ts";
import {type GarageCoversResponse} from "../model/garage/GarageCover.ts";
import {type GarageLabelRequest, type GarageRequest} from "../model/garage/api/GarageRequest.ts";
import {type GarageLabelsResponse} from "../model/garage/GarageLabel.ts";
import {type DealershipDetails, type DealershipCover} from "../model/garage/DealershipDetails.ts";
import {type UniqueNo} from "../model/core/UniqueNo.ts";
import {type ModifyDealershipRequest} from "../model/garage/api/Dealership.ts";
import {type LegalTextsResponse, type UpdateLegalTextsRequest} from "../model/garage/api/LegalTexts.ts";

class GarageService {
    api: AxiosInstance;

    constructor () {
        this.api = createApi("/b4cockpit/");
    }

    async getGarageDetail (garageNo: string): Promise<EditableGarageModel> {
        return await this.api.get(`/garage/details/${garageNo}`)
            .then(response => response.data as EditableGarageModel);
    }

    async getDealerships (garageNo: string): Promise<DealershipCover[]> {
        return await this.api.get(`/dealerships/${garageNo}`)
            .then(response => response.data);
    }

    async getDealership (dealershipNo: string): Promise<DealershipDetails> {
        return await this.api.get(`/dealership/${dealershipNo}`)
            .then(response => response.data);
    }

    async modifyDealership (request: ModifyDealershipRequest): Promise<UniqueNo> {
        return await this.api.post("/dealership", request)
            .then(response => response.data);
    }

    async getLegalTexts (dealerNo: string): Promise<LegalTextsResponse> {
        return await this.api.get(`/dealership/${dealerNo}/legal-texts`)
            .then(response => response.data);
    }

    async saveLegalTexts (dealerNo: string, request: UpdateLegalTextsRequest): Promise<void> {
        await this.api.post(`/dealership/${dealerNo}/legal-texts`, request)
            .then(response => response.data);
    }

    async saveGarageDetail (garageDetail: EditableGarageModel, image?: File): Promise<void> {
        const formData = new FormData();
        formData.append("request", new Blob([JSON.stringify(garageDetail)], {type: "application/json"}));
        if (image) {
            formData.append("image", image);
        }
        const config = {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        };
        await this.api.post(`/garage/details/${garageDetail.garageDetails.garageNo.index}`, formData, config)
            .then(response => response.data);
    }

    async getGarageCovers (request: GarageRequest): Promise<GarageCoversResponse> {
        return await this.api.post<GarageCoversResponse>("/garages", request)
            .then(response => response.data);
    }

    async getGarageLabels (params: {
        fuzzy?: string;
    }, config?: AxiosRequestConfig): Promise<GarageLabelsResponse> {
        const request: GarageLabelRequest = {
            fuzzy: params.fuzzy ?? ""
        };
        return await this.api.post<GarageLabelsResponse>("/garageLabels", request, config)
            .then(response => response.data);
    }
}

const garageServiceInstance = new GarageService();

export default garageServiceInstance;
